import { CheckoutAnalyticsService } from '@analytics/feature';
import { Injectable } from '@angular/core';
import { CheckoutValidator, PaymentMode, ValidationErrorKeys, ValidationErrors } from '@checkout/const';
import { PaymentModeFacade } from '@checkout/data-access';
import { PaymentModeCodes } from '@payment/const';
import { CurrentProfileService } from '@user/feature';
import { Observable, combineLatest } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { TimeSlotsService } from './time-slots.service';

@Injectable({
  providedIn: 'root',
})
export class PaymentModeService implements CheckoutValidator {
  constructor(
    private facade: PaymentModeFacade,
    private currentProfileService: CurrentProfileService,
    private checkoutAnalyticsService: CheckoutAnalyticsService,
    private timeSlotsService: TimeSlotsService,
  ) {}

  getList(): Observable<PaymentMode[]> {
    return this.facade.getList();
  }

  getListLoading(): Observable<boolean> {
    return this.facade.getListLoading();
  }

  getListError(): Observable<boolean> {
    return this.facade.getListError();
  }

  getListSuccess(): Observable<boolean> {
    return this.facade.getListSuccess();
  }

  getActivePayment(): Observable<PaymentMode | undefined> {
    return combineLatest({ availablePayments: this.getList(), activePaymentCode: this.getActivePaymentCode() }).pipe(
      map(
        ({ availablePayments, activePaymentCode }) =>
          availablePayments && availablePayments.find((payment: PaymentMode) => payment.code === activePaymentCode),
      ),
    );
  }

  getActivePaymentCode(): Observable<string | undefined> {
    return this.facade.getActivePaymentCode();
  }

  setActivePaymentMode(code: string): void {
    this.facade.setActivePaymentMode(code);
  }

  resetActivePaymentMode(): void {
    this.facade.resetActivePaymentMode();
  }

  getPaymentMode(code: PaymentModeCodes): Observable<PaymentMode | undefined> {
    return this.getList().pipe(
      filter(Boolean),
      map((paymentModes: PaymentMode[]) => paymentModes.find((paymentMode: PaymentMode) => paymentMode.code === code)),
    );
  }

  validate(): Observable<ValidationErrors> {
    return combineLatest({
      profile: this.currentProfileService.get(),
      activePayment: this.getActivePayment(),
      availablePayments: this.getList(),
      timeSlotsIsEmpty: this.timeSlotsService.isEmpty(),
    }).pipe(
      map(({ profile, activePayment, availablePayments, timeSlotsIsEmpty }) => {
        const validationErrors: ValidationErrors = {};

        if (!profile.isLegal && !activePayment && availablePayments.length > 0 && !timeSlotsIsEmpty) {
          validationErrors[ValidationErrorKeys.PAYMENT_REQUIRED] = 'Необходимо выбрать способ оплаты';
          this.checkoutAnalyticsService.alertNeedPaymentMode();
        }

        return validationErrors;
      }),
    );
  }

  initCardBindingOnExternalAddress(): void {
    this.facade.initCardBindingOnExternalAddress();
  }
}
